import { LoadingParams, type ListResponse } from 'framework/list';
import { useDateService } from 'framework/utils';
import { useProjectDataContext } from 'domain/shared/newProject';
import { usePeopleManagementApi } from 'infrastructure/api';
import { ComplianceDocumentType } from 'domain/shared/compliance';
import {
  buildLatestComplianceDocuments,
  mapComplianceDocumentForList,
} from 'domain/shared/compliance/registry/complianceDocumentHelpers';
import {
  buildDocumentFilter,
  buildProjectPoliciesData,
  mapProjectPoliciesDocument,
  mergeDocumentsById,
} from './helpers';
import { ProjectPolicyListItem } from './types';

const useProjectPoliciesData = () => {
  const { data: project } = useProjectDataContext();
  const { sendPostRequest } = usePeopleManagementApi();
  const { getMomentDateObj, getTimeDifference } = useDateService();

  const loadPoliciesResponse = async (params: LoadingParams): Promise<ListResponse<ComplianceDocumentType>> => {
    const response = await sendPostRequest('/policies/search', params);

    return response.json();
  };

  const getDataMethod = async (params: LoadingParams): Promise<ListResponse<ProjectPolicyListItem>> => {
    const projectPoliciesData = buildProjectPoliciesData(project);

    const [projectResponse, accountResponse] = await Promise.all([
      loadPoliciesResponse({
        ...params,
        filter: buildDocumentFilter(params, {
          relatedEmployeeProjectIds: {
            in: [projectPoliciesData.projectLegacyId],
          },
        }),
      }),
      projectPoliciesData.currentAccountId
        ? loadPoliciesResponse({
            ...params,
            filter: buildDocumentFilter(params, {
              relatedEmployeeAccountIds: {
                in: [projectPoliciesData.currentAccountId],
              },
            }),
          })
        : Promise.resolve(null),
    ]);

    const mergedDocuments = mergeDocumentsById(projectResponse.result, accountResponse?.result);
    const latestDocuments = buildLatestComplianceDocuments(
      mergedDocuments.map(item => mapComplianceDocumentForList(item)),
      getMomentDateObj,
      getTimeDifference,
    );
    const result = latestDocuments.map(document => mapProjectPoliciesDocument(document, projectPoliciesData));

    return {
      ...projectResponse,
      result,
    };
  };

  return {
    getDataMethod,
  };
};

export default useProjectPoliciesData;
